import { createEntityAdapter, createSelector, EntityState } from '@reduxjs/toolkit';
import {
  FromTourSuggestion,
  RoutingMethod,
  StopsOfDay,
  Tour,
  TourBundle,
  TourDetail,
  TourEditData,
  TourFetchingType,
  TourNumber,
  TourPlan,
  TourPlanningData,
  TourReason,
  TourStudentMatrixTour,
  TourSuitableAssignmentOptions,
  TourTimelineData,
  TourTransformationResult,
} from '../../@types/tour';
import { apiSlice } from './apiSlice';
import { RootState } from '../store';
import { getApplicationTimestamp } from '../../utils/applicationTimestamp';
import { ResponseContext } from '../../@types/responseContext';
import { LocalDateInput, serializeLocalDateFields, toLocalDateString } from '../../utils/localDate';

const tourAdapter = createEntityAdapter<Tour>();
const initialState = tourAdapter.getInitialState();

const tourBundleAdapter = createEntityAdapter<TourBundle>();
const initialStateBundle = tourBundleAdapter.getInitialState();

const tourNumberAdapter = createEntityAdapter<TourNumber>();
const initialStateTourNumber = tourNumberAdapter.getInitialState();

const tourPlanAdapter = createEntityAdapter<TourPlan>();
const initialStateTourPlan = tourPlanAdapter.getInitialState();

export const tourListApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getTourByFetchingType: builder.query<
      EntityState<Tour, string>,
      {
        tourFetchingType: TourFetchingType;
        showExtendedAttributes: boolean;
        /**
         * If true, tours are filtered by the globally selected date and time-dependent attributes
         * are resolved at that date. If false, all matching tours are loaded and their
         * time-dependent attributes are resolved at each tour's first day of validity.
         */
        useSelectedDate: boolean;
      }
    >({
      query: ({ tourFetchingType, showExtendedAttributes, useSelectedDate }) =>
        useSelectedDate
          ? `/tours/all/date/${getApplicationTimestamp()}/${tourFetchingType}?isExtended=${showExtendedAttributes}`
          : `/tours/all/${tourFetchingType}?isExtended=${showExtendedAttributes}`,
      transformResponse: (response: Tour[]) => tourAdapter.setAll(initialState, response),
      providesTags: ['Tour'],
    }),
    getToursByTourReason: builder.query<Tour[], { timestamp: number; tourReason: TourReason }>({
      query: ({ timestamp, tourReason }) => `/tours/all-by-tour-reason/${timestamp}/${tourReason}`,
      providesTags: ['Tour'],
    }),
    getTourStopsOfDay: builder.query<
      StopsOfDay[],
      {
        tourId: string;
        isTempTour: boolean;
      }
    >({
      query: ({ tourId, isTempTour }) =>
        isTempTour
          ? `/tours/temp/stops-of-day/${tourId}`
          : `/tours/stops-of-day/${tourId}/${getApplicationTimestamp()}`,
    }),
    getTourPlans: builder.query<EntityState<TourPlan, string>, void>({
      query: () => `/tours/tour-plans/all`,
      transformResponse: (response: TourPlan[]) =>
        tourPlanAdapter.setAll(initialStateTourPlan, response),
      providesTags: ['TourPlan'],
    }),
    getTourBundles: builder.query<EntityState<TourBundle, string>, void>({
      query: () => '/tours/bundle/all',
      transformResponse: (response: TourBundle[]) =>
        tourBundleAdapter.setAll(initialStateBundle, response),
      providesTags: ['TourBundle'],
    }),
    getTourNumbers: builder.query<EntityState<TourNumber, string>, void>({
      query: () => '/tours/numbers/all',
      transformResponse: (response: TourNumber[]): EntityState<TourNumber, string> => {
        return tourNumberAdapter.setAll(tourNumberAdapter.getInitialState(), response);
      },
      providesTags: ['TourNumber'],
    }),
    getTourDetails: builder.query<TourDetail, string>({
      query: (id) => ({
        url: `/tours/detail/${id}/${getApplicationTimestamp()}`,
        method: 'GET',
      }),
    }),
    getTourEditData: builder.query<TourEditData, string>({
      query: (id) => ({
        url: `/tours/edit-data/${id}/${getApplicationTimestamp()}`,
        method: 'GET',
      }),
      providesTags: ['TourEditData'],
    }),
    getToursSuitableAssignments: builder.query<TourSuitableAssignmentOptions, string>({
      query: (idsAndTimeInstant) => ({
        url: `/tours/suitable-assignments/${idsAndTimeInstant}`,
        method: 'GET',
      }),
    }),
    planTour: builder.mutation({
      query: (planTourRequest) => ({
        url: '/tours/plan',
        method: 'POST',
        body: serializeLocalDateFields(planTourRequest, ['from', 'until']),
      }),
      invalidatesTags: ['Tour', 'TourAll', 'TourPlan', 'LocationProviderCreditStatus'],
    }),
    updateTourBasicAttributes: builder.mutation({
      query: ({ id, tourBasicData }) => ({
        url: `tours/${id}/basic`,
        method: 'PUT',
        body: serializeLocalDateFields(tourBasicData, ['validFrom', 'validUntil']),
      }),
      invalidatesTags: ['Tour', 'TourAll', 'TourEditData'],
    }),
    updateTourCost: builder.mutation({
      query: ({ id, tourCostData }) => ({
        url: `/tours/${id}/cost`,
        method: 'PUT',
        body: serializeLocalDateFields(tourCostData, ['changesValidFrom']),
      }),
      invalidatesTags: ['Tour', 'TourAll', 'TourEditData'],
    }),
    updateMultipleToursMinutesAroundSchool: builder.mutation({
      query: ({ minutesAroundSchool }) => ({
        url: `/tours/minutes-around-school`,
        method: 'PUT',
        body: minutesAroundSchool,
      }),
      invalidatesTags: ['Tour', 'TourAll', 'TourEditData'],
    }),
    updateMultipleToursBasicAttributes: builder.mutation({
      query: ({ ids, tourBasicData }) => ({
        url: `/tours/tour-basic${ids}`,
        method: 'PUT',
        body: serializeLocalDateFields(tourBasicData, ['validFrom', 'validUntil']),
      }),
      invalidatesTags: ['Tour', 'TourAll', 'TourEditData'],
    }),
    addAssignmentToTour: builder.mutation({
      query: ({ id, tourAssignment }) => ({
        url: `/tours/${id}/add-assignment`,
        method: 'POST',
        body: serializeLocalDateFields(tourAssignment, ['validFrom']),
      }),
      invalidatesTags: ['Tour', 'TourAll', 'TourEditData'],
    }),
    addAssignmentsToTours: builder.mutation({
      query: ({ ids, tourAssignments }) => ({
        url: `/tours/add-assignments${ids}`,
        method: 'POST',
        body: serializeLocalDateFields(tourAssignments, ['changesValidFrom']),
      }),
      invalidatesTags: ['Tour', 'TourAll', 'TourEditData'],
    }),
    deleteAssignment: builder.mutation({
      query: ({ tourId, assignmentId, assignmentType }) => ({
        url: `/tours/${tourId}/assignment/${assignmentType}/${assignmentId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Tour', 'TourAll', 'TourEditData'],
    }),
    updateMultipleToursCosts: builder.mutation({
      query: (tourCosts) => ({
        url: '/tours/tour-cost',
        method: 'PUT',
        body: serializeLocalDateFields(tourCosts, ['validFrom']),
      }),
      invalidatesTags: ['Tour', 'TourAll', 'TourEditData'],
    }),
    updateTourDays: builder.mutation({
      query: ({ tourDays, tourId }) => ({
        url: `/tours/${tourId}/tour-days`,
        method: 'PUT',
        body: serializeLocalDateFields(tourDays, ['validFrom']),
      }),
      invalidatesTags: ['Tour', 'TourAll', 'TourEditData'],
    }),
    createManuellTour: builder.mutation({
      query: ({ tour }) => ({
        url: '/tours',
        method: 'POST',
        body: serializeLocalDateFields(tour, ['validFrom', 'validUntil']),
      }),
      invalidatesTags: ['Tour', 'LocationProviderCreditStatus'],
    }),
    deleteTours: builder.mutation({
      query: ({ ids, from }) => ({
        url: `/tours`,
        method: 'DELETE',
        body: { ids, from },
      }),
      invalidatesTags: ['Tour', 'TourAll', 'TourEditData'],
    }),
    deleteTour: builder.mutation({
      query: ({ id, timestamp }) => ({
        url: `/tours/${id}/${timestamp}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Tour', 'TourAll', 'TourEditData'],
    }),
    transformPlannedTours: builder.mutation({
      query: ({ ids, from }) => ({
        url: `/tours/transform`,
        method: 'POST',
        body: serializeLocalDateFields({ ids, from }, ['from']),
      }),
      invalidatesTags: ['Tour', 'TourAll'],
    }),
    rerouteTours: builder.mutation<unknown, { ids: string[]; method: RoutingMethod }>({
      query: ({ ids, method }) => ({
        url: `/tours/reroute?method=${method}`,
        method: 'POST',
        body: { ids },
      }),
      invalidatesTags: ['Tour', 'TourAll', 'TourPlan'],
    }),
    getTransformationResult: builder.query<TourTransformationResult, string>({
      query: (id) => ({
        url: `/tours/transform/${id}`,
        method: 'GET',
      }),
    }),
    createTourBundle: builder.mutation({
      query: ({ tourBundle }) => ({
        url: `tours/bundle`,
        method: 'POST',
        body: tourBundle,
      }),
      invalidatesTags: ['Tour', 'TourBundle'],
    }),
    updateTourBundle: builder.mutation({
      query: ({ tourBundle }) => ({
        url: `tours/bundle`,
        method: 'PUT',
        body: tourBundle,
      }),
      invalidatesTags: ['Tour', 'TourBundle'],
    }),
    deleteTourBundles: builder.mutation({
      query: ({ ids }) => ({
        url: `/tours/bundle`,
        method: 'DELETE',
        body: { ids },
      }),
      invalidatesTags: ['Tour', 'TourBundle'],
    }),
    removeStudentFromTour: builder.mutation<
      Tour,
      { tourId: string; studentId: string; changeDate: number }
    >({
      query: ({ tourId, studentId, changeDate }) => ({
        url: `tours/remove-student/${tourId}/${studentId}/${changeDate}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Tour', 'LocationProviderCreditStatus', 'TourEditData', 'TourPlan'],
    }),
    removeMandatoryRoutingPointFromTour: builder.mutation<
      Tour,
      { tourId: string; placeId: string; changeDate: number }
    >({
      query: ({ tourId, placeId, changeDate }) => ({
        url: `tours/remove-mandatory-routing-point/${tourId}/${placeId}/${changeDate}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Tour', 'LocationProviderCreditStatus', 'TourEditData', 'TourPlan'],
    }),
    addStudentToTourAndReplan: builder.mutation<
      ResponseContext,
      { tourId: string; studentId: string; changeDate: number }
    >({
      query: ({ tourId, studentId, changeDate }) => ({
        url: `tours/add-student-replan/${tourId}/${studentId}/${changeDate}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Tour', 'LocationProviderCreditStatus', 'TourEditData', 'TourPlan'],
    }),
    addStudentToTourAndReroute: builder.mutation<
      ResponseContext,
      { tourId: string; studentId: string; changeDate: number }
    >({
      query: ({ tourId, studentId, changeDate }) => ({
        url: `tours/add-student-reroute/${tourId}/${studentId}/${changeDate}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Tour', 'LocationProviderCreditStatus', 'TourEditData', 'TourPlan'],
    }),
    addStudentToExtraOrDeviationTour: builder.mutation<
      Tour,
      { tourId: string; studentId: string; placeId: string }
    >({
      query: ({ tourId, studentId, placeId }) => ({
        url: `tours/add-student/${tourId}`,
        method: 'PUT',
        body: { studentId, placeId },
      }),
      invalidatesTags: ['Tour', 'LocationProviderCreditStatus', 'TourEditData', 'TourPlan'],
    }),
    addMultipleStudentsToExtraTour: builder.mutation<
      Tour,
      { tourId: string; placeId: string; ids: string[]; from: LocalDateInput }
    >({
      query: ({ tourId, placeId, ids, from }) => ({
        url: `tours/add-multiple-students/${tourId}/${placeId}`,
        method: 'PUT',
        body: { ids, from: toLocalDateString(from) },
      }),
      invalidatesTags: ['Tour', 'LocationProviderCreditStatus', 'TourEditData'],
    }),
    addMandatoryRoutingPointToTour: builder.mutation<
      ResponseContext,
      { tourId: string; placeId: string; changeDate: number }
    >({
      query: ({ tourId, placeId, changeDate }) => ({
        url: `tours/add-mandatory-routing-point/${tourId}/${placeId}/${changeDate}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Tour', 'LocationProviderCreditStatus', 'TourEditData'],
    }),
    reorderStopsOfTour: builder.mutation<Tour, { tourId: string; ids: string[] }>({
      query: ({ tourId, ids }) => ({
        url: `tours/reorder-stops/${tourId}`,
        method: 'PUT',
        body: { ids },
      }),
      invalidatesTags: ['Tour', 'LocationProviderCreditStatus', 'TourEditData', 'TourPlan'],
    }),
    getTempTours: builder.query<TourDetail[], { tourId: string }>({
      query: ({ tourId }) => ({
        url: `/tours/temp-tours/${tourId}`,
        method: 'GET',
      }),
    }),
    getTourPlanningDataFromTour: builder.query<
      TourPlanningData,
      { tourId: string | null | undefined }
    >({
      query: ({ tourId }) => ({
        url: `/tours/tour-planning-data-from-tour/${tourId}`,
        method: 'GET',
      }),
    }),
    saveTempTour: builder.mutation<void, { tourId: string; from: number }>({
      query: ({ tourId, from }) => ({
        url: `tours/save-temp/${tourId}/${from}`,
        method: 'PUT',
      }),
      invalidatesTags: ['Tour', 'TourPlan'],
    }),
    discardTempTour: builder.mutation<void, { tourId: string }>({
      query: ({ tourId }) => ({
        url: `tours/temp/discard/${tourId}`,
        method: 'PUT',
      }),
    }),
    getAffectedToursOfStudentByTimestamp: builder.query<
      Tour[],
      { studentId: string; timestamp: number }
    >({
      query: ({ studentId, timestamp }) => `/tours/wizard/${studentId}/${timestamp}`,
    }),
    addStudentFromAllAffectedToursAndReplan: builder.mutation<
      Tour,
      { studentId: string; dateOfChangeTimestamp: number; ids: string[] }
    >({
      query: ({ studentId, dateOfChangeTimestamp, ids }) => ({
        url: `/tours/wizard/replan/${studentId}/${dateOfChangeTimestamp}`,
        method: 'POST',
        body: { ids },
      }),
      invalidatesTags: ['LocationProviderCreditStatus'],
    }),
    removeStudentFromAllAffectedToursAndReplan: builder.mutation<
      Tour,
      { studentId: string; dateOfChangeTimestamp: number; ids: string[] }
    >({
      query: ({ studentId, dateOfChangeTimestamp, ids }) => ({
        url: `/tours/wizard/remove-student/${studentId}/${dateOfChangeTimestamp}`,
        method: 'POST',
        body: { ids },
      }),
      invalidatesTags: ['LocationProviderCreditStatus'],
    }),
    getAlternativeTours: builder.query<
      Tour[],
      {
        studentId: string;
        placeId: string;
        isAdvancedFiltering: boolean;
        isSingleDay: boolean;
        tourType: string;
        validFrom: string;
        validUntil: string;
      }
    >({
      query: ({
        studentId,
        placeId,
        isAdvancedFiltering,
        isSingleDay,
        tourType,
        validFrom,
        validUntil,
      }) => ({
        url: `/tours/wizard/alternative-tours/${studentId}/${placeId}?isAdvancedFiltering=${isAdvancedFiltering}&isSingleDay=${isSingleDay}&tourType=${tourType}&validFrom=${toLocalDateString(validFrom as LocalDateInput)}&validUntil=${toLocalDateString(validUntil as LocalDateInput)}`,
        method: 'GET',
      }),
    }),
    createTourForEvent: builder.mutation({
      query: ({ studentId, eventId, tour }) => ({
        url: `/tours/for-event/${studentId}/${eventId}`,
        method: 'POST',
        body: serializeLocalDateFields(tour, ['validFrom', 'validUntil']),
      }),
      invalidatesTags: ['Tour', 'StudentCalendar', 'LocationProviderCreditStatus'],
    }),
    addStudentToAlternativeTour: builder.mutation({
      query: ({ alternativeTourId, payload }) => ({
        url: `/tours/add-student-to-alternative/${alternativeTourId}`,
        method: 'PUT',
        body: serializeLocalDateFields(payload, ['date']),
      }),
      invalidatesTags: ['Tour', 'StudentCalendar', 'LocationProviderCreditStatus', 'TourPlan'],
    }),
    getFromTourSuggestions: builder.query<FromTourSuggestion[], string[]>({
      query: (tourIds) => ({
        url: '/tours/tour-wizard-suggestions',
        method: 'POST',
        body: { ids: tourIds },
      }),
    }),
    getTourStudentMatrixRows: builder.query<TourStudentMatrixTour[], string[]>({
      query: (tourIds) => ({
        url: '/tours/tour-student-matrix-rows',
        method: 'POST',
        body: { ids: tourIds },
      }),
    }),
    planFromToursForToTours: builder.mutation({
      query: (planTourRequest) => ({
        url: '/tours/plan',
        method: 'POST',
        body: serializeLocalDateFields(planTourRequest, ['from', 'until']),
      }),
      invalidatesTags: ['Tour', 'TourAll', 'TourPlan', 'LocationProviderCreditStatus'],
    }),
    getToursOfRoadblock: builder.query<
      Tour[],
      {
        roadblockId: string | undefined;
      }
    >({
      query: ({ roadblockId }) => ({
        url: `/tours/roadblock/${roadblockId || '-1'}`,
        method: 'GET',
        providesTags: ['RoadblockTour'],
      }),
      providesTags: ['RoadblockTour'],
    }),
    getToursByTourNumber: builder.query<TourTimelineData[], { tourNumber: string }>({
      query: ({ tourNumber }) => `/tours/all-by-tour-number/${tourNumber}`,
    }),
  }),
});

export const {
  useGetTourByFetchingTypeQuery,
  usePlanTourMutation,
  useUpdateTourBasicAttributesMutation,
  useUpdateTourCostMutation,
  useUpdateMultipleToursMinutesAroundSchoolMutation,
  useAddAssignmentToTourMutation,
  useAddAssignmentsToToursMutation,
  useUpdateMultipleToursBasicAttributesMutation,
  useDeleteAssignmentMutation,
  useDeleteToursMutation,
  useDeleteTourMutation,
  useGetTourBundlesQuery,
  useDeleteTourBundlesMutation,
  useGetTourNumbersQuery,
  useGetTourPlansQuery,
  useCreateTourBundleMutation,
  useUpdateTourBundleMutation,
  useGetTourStopsOfDayQuery,
  useGetTourDetailsQuery,
  useRemoveStudentFromTourMutation,
  useAddStudentToTourAndReplanMutation,
  useAddStudentToTourAndRerouteMutation,
  useAddStudentToExtraOrDeviationTourMutation,
  useReorderStopsOfTourMutation,
  useTransformPlannedToursMutation,
  useRerouteToursMutation,
  useGetTransformationResultQuery,
  useSaveTempTourMutation,
  useDiscardTempTourMutation,
  useGetTempToursQuery,
  useGetTourEditDataQuery,
  useGetToursSuitableAssignmentsQuery,
  useUpdateTourDaysMutation,
  useUpdateMultipleToursCostsMutation,
  useGetAffectedToursOfStudentByTimestampQuery,
  useAddStudentFromAllAffectedToursAndReplanMutation,
  useRemoveStudentFromAllAffectedToursAndReplanMutation,
  useGetAlternativeToursQuery,
  useCreateTourForEventMutation,
  useAddStudentToAlternativeTourMutation,
  useGetTourPlanningDataFromTourQuery,
  useGetFromTourSuggestionsQuery,
  useGetTourStudentMatrixRowsQuery,
  usePlanFromToursForToToursMutation,
  useAddMandatoryRoutingPointToTourMutation,
  useRemoveMandatoryRoutingPointFromTourMutation,
  useGetToursByTourReasonQuery,
  useAddMultipleStudentsToExtraTourMutation,
  useGetToursOfRoadblockQuery,
  useGetToursByTourNumberQuery,
} = tourListApiSlice;

export const selectTourBundleResult = tourListApiSlice.endpoints.getTourBundles.select();
const selectTourBundleData = createSelector(
  selectTourBundleResult,
  (TourBundleResult) => TourBundleResult.data
);

export const { selectAll: selectAllTourBundle, selectById: selectTourBundleById } =
  tourBundleAdapter.getSelectors<RootState>(
    (state) => selectTourBundleData(state) ?? initialStateBundle
  );

export const selectTourNumberResult = tourListApiSlice.endpoints.getTourNumbers.select();
const selectTourNumberData = createSelector(
  selectTourNumberResult,
  (TourNumberResult) => TourNumberResult.data
);

export const { selectAll: selectAllTourNumbers, selectById: selectTourNumberById } =
  tourNumberAdapter.getSelectors<RootState>(
    (state) => selectTourNumberData(state) ?? initialStateTourNumber
  );

export const selectTourPlanResult = tourListApiSlice.endpoints.getTourPlans.select();
const selectTourPlanData = createSelector(
  selectTourPlanResult,
  (TourBundleResult) => TourBundleResult.data
);

export const { selectAll: selectAllTourPlans, selectById: selectTourPlanById } =
  tourPlanAdapter.getSelectors<RootState>(
    (state) => selectTourPlanData(state) ?? initialStateTourPlan
  );

export const useGetAllToursByFetchingType = (
  tourFetchingType: TourFetchingType,
  showExtendedTourAttributes: boolean,
  useSelectedDate: boolean
) => {
  const { data, isSuccess } = useGetTourByFetchingTypeQuery({
    tourFetchingType,
    showExtendedAttributes: showExtendedTourAttributes,
    useSelectedDate,
  });
  let tours: Tour[] = [];
  if (isSuccess) {
    tours = fillTourList(data);
  }
  return tours;
};

/**
 * Loads all tours matching the fetching type without filtering by the globally selected date.
 * Time-dependent attributes, such as assignments, are resolved at the first day of validity of
 * each tour.
 */
export const useGetAllTours = (tourFetchingType: TourFetchingType) =>
  useGetAllToursByFetchingType(tourFetchingType, true, false);

const fillTourList = (tourData: EntityState<Tour, string>) => {
  const tourList: Tour[] = [];
  Object.values(tourData.entities).forEach((elem) => tourList.push(elem));
  return tourList;
};
